import { Component } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { combineLatest, interval, of } from "rxjs";
import { filter, map, startWith, switchMap } from "rxjs/operators";
import { RaygunService } from "./configuration/services/raygun.service";
import { RideOpsUiService } from "./configuration/services/rideops-ui.service";
import { UpdateService } from "./configuration/services/update.service";
import { UtilityService } from "./configuration/services/utility.service";
import { LocationStateQuery } from "./configuration/state/location-state.query";
import { LocationStateService } from "./configuration/state/location-state.service";
import { MeasurementService } from "./configuration/services/measurement.service";
import { FeatureFlagService } from "./features/feature-flag/services/feature-flag.service";
import { ChecklistStateQuery } from "./features/checklists/state/checklist-state.query";

export type V2ReadinessBannerState = "not-ready" | "preopening" | "ready" | "downtime";

export interface V2ReadinessBanner {
	state: V2ReadinessBannerState;
	labelKey?: string;
	downtimeStartedAt?: string;
}

@Component({
	selector: "app-root",
	templateUrl: "app.component.html",
	styleUrls: ["app.component.scss"],
})
export class AppComponent {
	constructor(
		updateService: UpdateService,
		raygunService: RaygunService,
		locationStateService: LocationStateService,
		measurementService: MeasurementService,
		featureFlagService: FeatureFlagService,
		public rideOpsUi: RideOpsUiService,
		private locationStateQuery: LocationStateQuery,
		private checklistStateQuery: ChecklistStateQuery,
		private utilityService: UtilityService,
		private router: Router,
	) {
		raygunService.activate();
		updateService.activate();

		locationStateService.activate();
		measurementService.activate();
		featureFlagService.activate();
	}

	v2NavCollapsed$ = combineLatest([this.rideOpsUi.isV2Active$, this.locationStateQuery.operatorIsSelected$]).pipe(
		map(([isV2, operatorIsSelected]) => isV2 === true && operatorIsSelected !== true),
	);

	isSetupRoute$ = this.router.events.pipe(
		filter((event): event is NavigationEnd => event instanceof NavigationEnd),
		map((event) => event.urlAfterRedirects),
		startWith(this.router.url),
		map((url) => {
			const path = url.split(/[?#]/)[0] || "/";
			return path === "/setup" || path.startsWith("/setup/");
		}),
	);

	v2ReadinessBanner$ = combineLatest([
		this.rideOpsUi.isV2Active$,
		this.isSetupRoute$,
		this.locationStateQuery.rideOpsKeyIsSet$,
		this.locationStateQuery.locationIsOpen$,
		this.locationStateQuery.selectState$,
		this.locationStateQuery.downtime$,
		this.locationStateQuery.isClearedForOperation$,
		this.locationStateQuery.mandatoryPositionsWithoutAttendant$,
		this.locationStateQuery.isClearedForPreopening$,
		this.locationStateQuery.isClearedForOperationDetails$,
		this.checklistStateQuery.preopeningChecklistSubmitted$,
	]).pipe(
		map(
			([
				isV2,
				isSetupRoute,
				rideOpsKeyIsSet,
				isOpen,
				state,
				downtime,
				isClearedForOperation,
				mandatoryPositionsWithoutAttendant,
				isClearedForPreopening,
				clearedForOperationDetails,
				preopeningChecklistSubmitted,
			]): V2ReadinessBanner | null => {
				if (!isV2 || isSetupRoute || rideOpsKeyIsSet !== true) {
					return null;
				}

				if (state === "Down" && downtime != null) {
					return {
						state: "downtime",
						downtimeStartedAt: downtime.updated,
					};
				}

				const positionsFilled = (mandatoryPositionsWithoutAttendant?.length ?? 0) === 0;
				const readyToOperate = isClearedForOperation === true && positionsFilled;

				if (isOpen && readyToOperate) {
					return null;
				}

				if (readyToOperate) {
					return { state: "ready" };
				}

				if (isClearedForPreopening === true && positionsFilled) {
					const preopeningChecklist = clearedForOperationDetails?.preopeningChecklists?.[0];
					const isSubmitted =
						preopeningChecklistSubmitted != null || preopeningChecklist?.completed != null;
					const awaitingValidation =
						isSubmitted &&
						preopeningChecklist?.requiresApproval === true &&
						preopeningChecklist.isCleared !== true &&
						preopeningChecklist.approved == null;

					return {
						state: "preopening",
						labelKey: awaitingValidation
							? "LOCATION_CLOSED_V2__STATUS_PILL_PREOPENING_AWAITING_REVIEW"
							: "LOCATION_CLOSED_V2__STATUS_PILL_PREOPENING",
					};
				}

				return { state: "not-ready" };
			},
		),
	);

	v2DowntimeElapsed$ = this.v2ReadinessBanner$.pipe(
		switchMap((banner) => {
			if (banner?.state !== "downtime" || banner.downtimeStartedAt == null) {
				return of(null);
			}

			const downtimeStartedAt = banner.downtimeStartedAt;
			return interval(1000).pipe(
				startWith(0),
				map(() => this.utilityService.formatElapsedHms(downtimeStartedAt)),
			);
		}),
	);
}
